import type { FbContentType, GbpConfig, IgContentType, LinkPreview, PostMedia } from '../../../types/publishing';
import { destinationById, type PostDestinationId } from '../../../lib/publishing/destinations';
import type { ValidateIssue } from '../../../lib/publishing/validateWarnings';
import { ChannelIcon } from '../../ui/ChannelIcon';
import { FacebookPreview } from './FacebookPreview';
import { InstagramPreview } from './InstagramPreview';
import { GbpPreview } from './GbpPreview';

// The PRESENTATIONAL preview column, shared by the staff composer and the club's.
// It owns no data fetching and no composer state: the caller resolves wildcards,
// picks each destination's effective caption and media, and (for the staff) owns
// the debounced link-preview fetch. ⚠️ That split is the point — the staff
// wrapper (modules/publishing/previews/PostPreviews.tsx) calls a staff endpoint
// and takes a ComposerState, neither of which a club has.
//
// ⚠️ Warnings are matched by PLATFORM, not destination — Zernio's validate names
// only the platform, so two destinations on one platform show the same warning.

export interface PostPreviewItem {
  destination: PostDestinationId;
  content: string;
  media: PostMedia[];
  customised?: boolean;
  fbContentType: FbContentType;
  igContentType: IgContentType;
  gbp: GbpConfig;
  firstComment: string;
}

export function PostPreviewList({
  items,
  pageName,
  issues,
  facebookLinkPreview,
}: {
  items: PostPreviewItem[];
  pageName: string;
  issues: ValidateIssue[];
  /** Already fetched by the caller; null/absent simply omits the Facebook link card. */
  facebookLinkPreview?: LinkPreview | null;
}) {
  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400">
        Pick a destination to see a live preview.
      </div>
    );
  }
  return (
    <div className="space-y-6">
      {items.map((t) => {
        const destination = destinationById(t.destination);
        const platformIssues = issues.filter((i) => i.platform === destination.platform);
        const isStory = destination.placement === 'story';
        return (
          <div key={t.destination} className="space-y-2">
            <div className="flex items-center gap-2">
              <ChannelIcon channel={destination.platform} className="h-4 w-4" />
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{destination.label}</span>
              {t.customised && (
                <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">customised</span>
              )}
            </div>

            {destination.platform === 'facebook' && (
              <FacebookPreview
                pageName={pageName}
                content={t.content}
                media={t.media}
                contentType={isStory ? 'story' : t.fbContentType}
                firstComment={t.firstComment}
                linkPreview={isStory ? null : (facebookLinkPreview ?? null)}
              />
            )}
            {destination.platform === 'instagram' && (
              <InstagramPreview
                handle={pageName}
                content={t.content}
                media={t.media}
                contentType={isStory ? 'story' : t.igContentType}
                firstComment={t.firstComment}
              />
            )}
            {destination.platform === 'googlebusiness' && (
              <GbpPreview businessName={pageName} content={t.content} media={t.media} gbp={t.gbp} />
            )}

            {platformIssues.length > 0 && (
              <ul className="space-y-1">
                {platformIssues.map((i, idx) => (
                  <li
                    key={idx}
                    className={`rounded px-2 py-1 text-xs ${i.level === 'error' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}
                  >
                    {i.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
